import * as React from 'react';
import {
    ContemberClient,
    DialogProvider,
    EnvironmentContext,
    I18nProvider,
    IdentityContext,
    Pages,
    pathToRequestState,
    RequestProvider,
    RoutingContext,
    runReactApp,
    SectionTabsProvider,
    StyleProvider,
    Toaster,
    ToasterProvider,
    useSetSessionToken,
    useRedirect,
} from '@contember/admin';
import '@contember/admin/style.css';
import { ContextType } from 'react';
import { Environment } from '@contember/binding';
import { RoutingContextValue } from '@contember/admin/src/routing';
import { createRoot } from 'react-dom/client';

import { NavigationProvider } from './components/NavigationProvider';
import CleverMapsSDK from './components/cleverMapsSDK';

import './index.css';
import { User, UserManagerSettings } from 'oidc-client-ts';
import { AuthProvider, useAuth } from 'react-oidc-context';

type ContemberConnectionConfig = {
    sessionToken: string;
};

export const COGNITO: UserManagerSettings = {
    authority:
        'https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_KB967SND5',
    client_id: 'nois76ns356kres8lnqijf9i6',
    redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'email openid',
    accessTokenExpiringNotificationTimeInSeconds: 3600,
    // no revoke of "access token" (https://github.com/authts/oidc-client-ts/issues/262)
    revokeTokenTypes: ['refresh_token'],
    // no silent renew via "prompt=none" (https://github.com/authts/oidc-client-ts/issues/366)
    automaticSilentRenew: false,
};

const fakeIdentityContext: ContextType<typeof IdentityContext> = {
    identity: {
        id: '',
        otpEnabled: false,
        email: '',
        personId: '',
        permissions: {
            canCreateProject: false,
        },
        projects: [],
    },
    clearIdentity: () => {
        throw new Error('Not implemented');
    },
};

const Content: React.FC<ContemberConnectionConfig> = ({ sessionToken }) => {
    const setSessionToken = useSetSessionToken();
    React.useEffect(() => {
        setSessionToken(sessionToken);
    }, [sessionToken, setSessionToken]);
    return (
        <Pages>{import.meta.glob('./pages/**/*.tsx', { eager: true })}</Pages>
    );
};

const StableInnerApp = React.memo<ContemberConnectionConfig>(
    ({ sessionToken }) => {
        return (
            <NavigationProvider>
                <ContemberClient
                    apiBaseUrl={
                        import.meta.env.VITE_CONTEMBER_ADMIN_API_BASE_URL
                    }
                    sessionToken={sessionToken}
                    project={import.meta.env.VITE_CONTEMBER_ADMIN_PROJECT_NAME}
                    stage="live"
                >
                    <IdentityContext.Provider value={fakeIdentityContext}>
                        <CleverMapsSDK>
                            <Content sessionToken={sessionToken}></Content>
                        </CleverMapsSDK>
                    </IdentityContext.Provider>
                </ContemberClient>
            </NavigationProvider>
        );
    }
);

StableInnerApp.displayName = 'StableInnerApp';

const AuthedContemberApp = React.memo(() => {
    const auth = useAuth();

    if (auth.activeNavigator === 'signinSilent') {
        return <div>Signing you in...</div>;
    }

    if (auth.isLoading) {
        return <div>Loading...</div>;
    }

    if (auth.error) {
        return <div>Oops... {auth.error.message}</div>;
    }

    if (!auth.isAuthenticated) {
        // Set custom state before redirecting to signin
        const customState = {
            returnUrl: window.location.pathname + window.location.search,
        };

        auth.signinRedirect({ state: customState });
        return <div>Redirecting to login...</div>;
    }

    if (import.meta.env.VITE_CONTEMBER_ADMIN_SESSION_TOKEN !== undefined) {
        return (
            <StableInnerApp
                sessionToken={
                    import.meta.env.VITE_CONTEMBER_ADMIN_SESSION_TOKEN
                }
            />
        );
    }
    return <StableInnerApp sessionToken={auth?.user?.access_token} />;
});

AuthedContemberApp.displayName = 'AuthedContemberApp';

const routing: RoutingContextValue = {
    basePath: '/',
    routes: { index: { path: '/' } },
    defaultDimensions: {},
};

const EntrypointWithoutClient = React.memo(
    ({ children }: { children: React.ReactNode }) => {
        const rootEnv = Environment.create();

        return (
            <StyleProvider>
                <EnvironmentContext.Provider value={rootEnv}>
                    <I18nProvider
                        localeCode={undefined}
                        dictionaries={undefined}
                    >
                        <ToasterProvider>
                            <DialogProvider>
                                <NavigationProvider>
                                    <SectionTabsProvider>
                                        {children}
                                    </SectionTabsProvider>
                                </NavigationProvider>
                                <Toaster />
                            </DialogProvider>
                        </ToasterProvider>
                    </I18nProvider>
                </EnvironmentContext.Provider>
            </StyleProvider>
        );
    }
);

EntrypointWithoutClient.displayName = 'EntrypointWithoutClient';

const SecurityProvider = ({ children }) => {
    const redirect = useRedirect();
    return (
        <AuthProvider
            {...COGNITO}
            onSigninCallback={async (user: User): Promise<void> => {
                console.log(user, location);
                const customState = user.state as { returnUrl?: string };
                const url = new URL(
                    customState?.returnUrl ?? '/',
                    location.origin
                );
                redirect(pathToRequestState(routing, url.pathname, url.search));
            }}
        >
            {children}
        </AuthProvider>
    );
};

const App = () => {
    return (
        <EntrypointWithoutClient>
            <RoutingContext.Provider value={routing}>
                <RequestProvider>
                    <SecurityProvider>
                        <AuthedContemberApp />
                    </SecurityProvider>
                </RequestProvider>
            </RoutingContext.Provider>
        </EntrypointWithoutClient>
    );
};

runReactApp(<App />, null, (dom, react, onRecoverableError) =>
    createRoot(dom, { onRecoverableError }).render(react)
);
